import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	Tabs,
} from 'antd';
import { FC, Fragment } from 'react';
// import { getCustomerChecking } from '@/libs/fleet-api/services/customer';
import { useApp } from '@/App';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import ViewManagementShareholder from '@/libs/antd/components/ViewManagementShareholder';
import { FormAlert, useForm } from '@/libs/antd/form';
import formRule from '@/libs/antd/rules';
import {
	getProspectCustomerChecking,
	ProspectCustomerCheckingAppovalPayload,
	submitProspectCustomerCheckingApproval,
} from '@/libs/fleet-api/services/prospect';
import dayjs from 'dayjs';
import { formatCurrency } from '@/libs/antd/formater';
import GenericErrorMessage from '@/components/GenericErrorMessage';

export type FBDHCustomerCheckingPageProps = {
	prospectId: string;
};

const FBDHCustomerCheckingPage: FC<FBDHCustomerCheckingPageProps> = ({
	prospectId = '943P2024060009',
}) => {
	const { navigate, notification } = useApp();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openPreviewModal,
		openProspectDetail,
		openNegativeHistoryModal,
		openRejectHistoryModal,
	} = useModals();

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCustomerChecking', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCustomerChecking(id);
		},
	});

	const {
		mainInformation,
		customerNegativeHistory,
		customerRejectionHistory,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		prospectInformation,
		approvalHistory,
		slikCheck,
	} = data?.data || {};

	const isJF = mainInformation?.customerType?.id === 1;

	const { form, fieldName } = useForm<ProspectCustomerCheckingAppovalPayload>();

	const prospectCustomerCheckingApprovalMutation = useMutation({
		mutationFn: submitProspectCustomerCheckingApproval,
	});

	const isNegative =
		!!customerNegativeHistory?.length &&
		customerNegativeHistory[0].isActive === 'Active' &&
		customerNegativeHistory[0].negativeType === 'Bad';

	// const canSubmit = isNegative;

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle level={1}>Pengecekan Customer</FleetTitle>
			<Divider />
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								onClick={() =>
									openProspectDetail(mainInformation?.prospectId ?? prospectId)
								}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
								>
									{mainInformation?.customerGroup.value}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			<Tabs
				items={[
					{
						key: 'Informasi Prospect',
						label: 'Informasi Prospect',
						children: (
							<Fragment>
								<FleetTitle>Informasi Limit Pembiayaan Existing</FleetTitle>
								<Table
									pagination={false}
									scroll={{ x: 'max-content' }}
									loading={isLoading}
									bordered
									dataSource={
										prospectInformation?.existingFinancingLimitInformation.map(
											(record, key) => {
												return {
													...record,
													key,
												};
											},
										) ?? []
									}
									columns={[
										{
											title: 'No',
											render: (_, record) => record.no,
										},
										{
											title: 'No. MKL',
											render: (_, record) => record.napNo,
										},
										{
											title: 'Limit Pembiayaan Existing',
											render: (_, record) =>
												formatCurrency(record.existingFinancingLimit),
										},
										{
											title: 'Jenis Limit Pembiayaan',
											render: (_, record) => record.financingLimitType,
										},
										{
											title: 'Tipe Limit',
											render: (_, record) =>
												record.financingLimitCharacteristic,
										},
										{
											title: 'Expired Date',
											render: (_, record) =>
												dayjs(record.expiredDate).format('DD/MM/YYYY'),
										},
										{
											title: 'NTF',
											render: (_, record) => formatCurrency(record.ntf) || 0,
										},
										{
											title: 'OS Principal',
											render: (_, record) =>
												formatCurrency(record.outstandingPrincipal),
										},
										{
											title: 'Sisa Limit',
											render: (_, record) =>
												formatCurrency(record.outstandingFinancingLimit),
										},
										{
											title: 'Status',
											render: (_, record) => record.status,
										},
										// {
										// 	title: 'Select',
										// 	width: 1,
										// 	align: 'center',
										// 	render: () => (
										// 		<a style={{ display: 'block' }}>
										// 			<ArrowRightOutlined />
										// 		</a>
										// 	),
										// },
									]}
								/>
								<Divider />
								<FleetTitle>Informasi Limit Pengajuan Pembiayaan</FleetTitle>
								<Table
									bordered
									scroll={{ x: 'max-content' }}
									loading={isLoading}
									pagination={false}
									dataSource={
										prospectInformation?.financingApplicationLimitInformation.map(
											(record, key) => {
												return {
													...record,
													key,
												};
											},
										) ?? []
									}
									columns={[
										{
											title: 'No',
											align: 'center',
											render: (_, record) => record.no,
										},
										{
											title: 'Limit Pembiayaan Existing',
											render: (_, record) =>
												formatCurrency(record.existingFinancingLimit),
										},
										{
											title: 'Jenis Limit Pembiayaan',
											render: (_, record) => record.financingLimitType,
										},
										{
											title: 'OS Principal',
											render: (_, record) =>
												formatCurrency(record.outstandingPrincipal),
										},
										{
											title: 'Tujuan Pembiayaan',
											render: (_, record) => record.financingPurpose,
										},
										{
											title: 'Pengajuan Limit',
											render: (_, record) =>
												formatCurrency(record.limitApplication),
										},
										{
											title: 'Total Pengajuan Plafon',
											render: (_, record) =>
												formatCurrency(record.totalLimitApplication),
										},
										{
											title: 'Segmen Market',
											render: (_, record) => record.segmentMarket,
										},
									]}
								/>
							</Fragment>
						),
					},
					{
						key: 'Customer Checking',
						label: 'Customer Checking',
						children: (
							<Fragment>
								<FleetTitle>Management/Shareholder Listing</FleetTitle>
								<ViewManagementShareholder
									managements={customerCompanyManagementInformation ?? []}
									shareholders={customerCompanyShareholderInformation ?? []}
									style={{ marginBottom: 20 }}
								/>

								<FleetTitle>Reject Customer History</FleetTitle>
								<Table
									scroll={{ x: 'max-content' }}
									style={{
										marginBottom: 20,
									}}
									bordered
									loading={isLoading}
									pagination={false}
									dataSource={
										customerRejectionHistory?.map((reject, key) => {
											return {
												key,
												...reject,
											};
										}) ?? []
									}
									columns={[
										{
											title: 'No',
											render: (_, record) => record.no,
										},
										{
											title: 'Prospect ID',
											render: (_, record) => record.prospectId,
										},
										{
											title: 'Customer Name',
											render: (_, record) => record.customerName,
										},
										{
											title: 'Reject Status',
											align: 'center',
											render: (_, record) => record.rejectedStatus,
										},
										{
											title: 'Reject Date',
											align: 'center',
											render: (_, record) =>
												record.rejectedDate
													? dayjs(record.rejectedDate).format('DD/MM/YYYY')
													: '-',
										},
										{
											title: 'Rejected Process',
											align: 'center',
											render: (_, record) => record.rejectedProcess,
										},
										{
											title: 'Rejected By',
											align: 'center',
											render: (_, record) => record.rejectedBy,
										},
										{
											title: 'Detail Reject',
											align: 'center',
											render: (_, record) => (
												<a
													href="#"
													onClick={(e) => {
														e.preventDefault();
														openRejectHistoryModal(record.rejectedId);
													}}
												>
													<FileSearchOutlined />
												</a>
											),
										},
									]}
								/>

								<FleetTitle>Negative Customer History</FleetTitle>
								<Table
									style={{
										marginBottom: 20,
									}}
									bordered
									scroll={{ x: 'max-content' }}
									loading={isLoading}
									pagination={false}
									dataSource={
										customerNegativeHistory?.map((negative, key) => {
											return {
												key,
												...negative,
											};
										}) ?? []
									}
									columns={[
										{
											title: 'No',
											render: (_, record) => record.no,
										},
										{
											title: 'Prospect ID',
											render: (_, record) => record.prospectId ?? '-',
										},
										{
											title: 'Customer Name',
											render: (_, record) => record.customerName,
										},
										{
											title: 'Negative Status',
											align: 'center',
											render: (_, record) =>
												record.negativeStatus
													? record.negativeStatus === 'Bad' ||
														record.negativeStatus === 'Warning'
														? 'Yes'
														: 'No'
													: '-',
										},
										{
											title: 'Type',
											align: 'center',
											render: (_, record) => (
												<span
													className={`negative-badge status-${record.negativeType?.toLowerCase()}`}
												>
													{record.negativeType ?? '-'}
												</span>
											),
										},
										{
											title: 'Negative Date',
											align: 'center',
											render: (_, record) =>
												record.negativeDate
													? dayjs(record.negativeDate).format('DD/MM/YYYY')
													: '-',
										},
										{
											title: 'Source',
											align: 'center',
											render: (_, record) => record.source ?? '-',
										},
										{
											title: 'Active',
											align: 'center',
											render: (_, record) =>
												record.isActive === 'Active' ? 'Yes' : 'No',
										},
										{
											title: 'Detail Negative',
											align: 'center',
											render: (_, record) => (
												<a
													href="#"
													onClick={(e) => {
														e.preventDefault();
														openNegativeHistoryModal(record);
														// setNegativeModal(true);
													}}
												>
													<FileSearchOutlined />
												</a>
											),
										},
									]}
								/>
								{isNegative && (
									<Alert
										message="Customer tidak dapat melanjutkan proses karena masuk dalam kategori BAD,silakan ajukan penyimpangan"
										type="warning"
										className="custom-alert"
										showIcon
										style={{ marginBottom: 20 }}
									/>
								)}

								<FleetTitle>View Data Checking SLIK</FleetTitle>
								{isJF && (
									<Fragment>
										<DataRow
											items={[
												[
													[
														'Status Pengecekan SLIK',
														slikCheck?.slikCheckingStatus?.value,
													],
													slikCheck?.slikCheckingStatus?.id === 1
														? [
																'Tanggal Pengecekan SLIK',
																slikCheck.slikCheckingDate &&
																	dayjs(slikCheck.slikCheckingDate).format(
																		'DD/MM/YYYY',
																	),
															]
														: undefined,
												],
											]}
										/>
										{slikCheck?.slikCheckingDate &&
											dayjs().diff(slikCheck.slikCheckingDate, 'day') > 90 && (
												<FormAlert
													alerts={[
														'Data SLIK debitur Anda sudah melewati masa berlaku, mohon untuk melakukan pembaruan data SLIK agar dapat melanjutkan proses.',
													]}
												/>
											)}
										<Divider />
									</Fragment>
								)}
								<DataRow
									items={[
										[
											[
												'View Form FAPP',
												slikCheck?.slikFormFile.id ? (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															if (!slikCheck?.slikFormFile) return;
															openPreviewModal(slikCheck.slikFormFile);
														}}
													>
														Form FAPP
													</a>
												) : (
													'-'
												),
											],
											[
												'View Form PDP',
												slikCheck?.applicationLetterFile.id ? (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															if (!slikCheck?.applicationLetterFile) return;
															openPreviewModal(slikCheck.applicationLetterFile);
														}}
													>
														Form-PDP
													</a>
												) : (
													'-'
												),
											],
										],
									]}
								/>
								<Divider />
								<FleetTitle>Approval History</FleetTitle>
								<Table
									bordered
									loading={isLoading}
									pagination={false}
									dataSource={
										approvalHistory?.map((approval, key) => {
											return {
												key,
												...approval,
											};
										}) ?? []
									}
									columns={[
										{
											title: 'No',
											render: (_, record) => record.no,
										},
										{
											title: 'Name',
											render: (_, record) => record.approvalBy,
										},
										{
											title: 'Date',
											render: (_, record) => record.approvalDate,
										},
										{
											title: 'Process',
											render: (_, record) => record.approvalProcess,
										},
										{
											title: 'Status',
											align: 'center',
											render: (_, record) => {
												return (
													<span
														className={`review-status ${record.approvalStatus.toLowerCase()}`}
													>
														{record.approvalStatus}
													</span>
												);
											},
										},
										{
											title: 'Approval Notes',
											render: (_, record) => record.approvalNote,
										},
									]}
									style={{ marginBottom: 20 }}
								/>
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={form}
									onFinish={async (values) => {
										try {
											await prospectCustomerCheckingApprovalMutation.mutateAsync(
												{
													...values,
													prospectId: mainInformation!.prospectId,
												},
											);
											notification.success({
												message: 'Success',
												description:
													'Submit approval customer checking success',
											});
											navigate('FBDHCustomerCheckingListPage');
										} catch {
											notification.error({
												message: 'Failed',
												description: 'Submit approval customer checking failed',
											});
										}
									}}
								>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName('approvalStatus')}
												label="Approval Status"
												rules={[formRule.required()]}
											>
												<Select
													disabled={isNegative}
													options={[
														{
															value: 'Approve',
															label: 'Approve',
														},
														{
															value: 'Return',
															label: 'Return',
														},
													]}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												name={fieldName('additionalInformation')}
												label="Informasi Tambahan"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<Input.TextArea
													rows={4}
													disabled={isNegative}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Button
										type="primary"
										htmlType="submit"
										disabled={isNegative}
										loading={prospectCustomerCheckingApprovalMutation.isPending}
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 60,
										}}
									>
										NEXT
									</Button>
								</Form>
							</Fragment>
						),
					},
				]}
			/>
		</Fragment>
	);
};

export default FBDHCustomerCheckingPage;
